/*
 * HOLY PIT Refills V2
 * Isolated controller for scent and pack selection.
 * The existing Focal product-form remains responsible for Shopify Ajax cart handling.
 */
(function () {
  'use strict';

  if (window.customElements.get('hp-refill-selector')) return;

  const hpReviewCache = new Map();

  class HpRefillSelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.section = this.closest('[data-hp-v2-refills]');
      this.initializeReviewSummary();
      this.form = this.querySelector('form[action*="/cart/add"]');
      this.variantInput = this.querySelector('[data-hp-variant-id]');
      this.submitButton = this.querySelector('[data-hp-submit]');
      this.bundleSize = Number(this.section.dataset.bundleSize || 3);
      this.weeksPerRefill = Number(this.section.dataset.weeksPerRefill || 8);
      this.currency = this.section.dataset.currency || 'EUR';
      this.locale = this.section.dataset.locale || document.documentElement.lang || 'de-DE';
      this.productData = this.readJson('[data-hp-product-json]', {});
      this.variantMap = this.readJson('[data-hp-variant-map]', []);

      if (!this.form || !this.variantInput || !this.submitButton || !this.variantMap.length) {
        console.error('HOLY PIT Refills: Die Produktdaten konnten nicht geladen werden.');
        return;
      }

      this.selectedScent = this.getInitialScent();
      this.selectedPack = this.section.dataset.defaultPack === 'single' ? 'single' : 'bundle';

      this.clickHandler = this.onClick.bind(this);
      this.addEventListener('click', this.clickHandler);
      this.keyHandler = this.onKeyDown.bind(this);
      this.addEventListener('keydown', this.keyHandler);
      this.sectionClickHandler = this.onSectionClick.bind(this);
      this.section.addEventListener('click', this.sectionClickHandler);
      this.addedHandler = this.onAdded.bind(this);
      this.form.addEventListener('variant:added', this.addedHandler);

      this.initializeCountdowns();
      this.initializeMediaHighlight();
      this.initializeAboBox();
      this.initializePaymentDialog();
      this.removeSamplePreviewText();
      this.samplePreviewTimers = [500, 1500].map((delay) => window.setTimeout(() => this.removeSamplePreviewText(), delay));
      this.render();
    }

    disconnectedCallback() {
      this.dataset.initialized = 'false';
      if (this.reviewAbort) this.reviewAbort.abort();
      (this.reviewTimers || []).forEach((timer) => window.clearTimeout(timer));
      (this.reviewLinks || []).forEach(({ link, handler }) => link.removeEventListener('click', handler));
      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keydown', this.keyHandler);
      if (this.form) this.form.removeEventListener('variant:added', this.addedHandler);
      (this.countdownTimers || []).forEach((timer) => window.clearInterval(timer));
      (this.samplePreviewTimers || []).forEach((timer) => window.clearTimeout(timer));
      if (this.sectionClickHandler) this.section.removeEventListener('click', this.sectionClickHandler);
      if (this.mediaCarousel && this.mediaHighlightHandler) {
        this.mediaCarousel.removeEventListener('flickity:ready', this.mediaHighlightHandler);
        this.mediaCarousel.removeEventListener('flickity:slide-changed', this.mediaHighlightHandler);
      }
      if (this.aboDialog && this.aboCloseHandler) this.aboDialog.removeEventListener('close', this.aboCloseHandler);
      if (this.aboDialog && this.aboKeydownHandler) this.aboDialog.removeEventListener('keydown', this.aboKeydownHandler);
      if (this.paymentDialog && this.paymentCloseHandler) this.paymentDialog.removeEventListener('close', this.paymentCloseHandler);
      if (this.paymentDialog && this.paymentKeydownHandler) this.paymentDialog.removeEventListener('keydown', this.paymentKeydownHandler);
    }

    initializeReviewSummary() {
      this.reviewAbort = new AbortController();
      this.reviewTimers = [];
      this.reviewLinks = [];
      this.querySelectorAll('[data-hp-review-summary]').forEach((node) => {
        const link = node.querySelector('[data-hp-review-link]');
        if (link) {
          const handler = () => {
            const widget = document.querySelector('[data-reviewsio-widget="polaris"], .reviews-io-polaris-widget, #ReviewsWidget');
            if (!widget) return;
            widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
            widget.setAttribute('tabindex', '-1');
            widget.focus({ preventScroll: true });
          };
          link.addEventListener('click', handler);
          this.reviewLinks.push({ link, handler });
        }
        this.loadReviewSummary(node, 0, this.reviewAbort.signal);
      });
    }

    async loadReviewSummary(node, attempt, lifecycleSignal) {
      const store = node.dataset.reviewStore;
      const group = node.dataset.reviewGroup;
      if (!store || !group || lifecycleSignal.aborted) return;
      const key = store + ':' + group;
      const cached = hpReviewCache.get(key);
      if (cached && Date.now() - cached.time < 300000) {
        this.displayReviewSummary(node, cached.stats);
        return;
      }
      const request = new AbortController();
      const cancel = () => request.abort();
      lifecycleSignal.addEventListener('abort', cancel, { once: true });
      const timeout = window.setTimeout(cancel, 10000);
      try {
        const url = new URL('https://api.reviews.io/product/review');
        url.searchParams.set('store', store);
        url.searchParams.set('sku', group);
        const response = await fetch(url.toString(), { signal: request.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Review summary unavailable');
        const data = await response.json();
        const average = Number(data.stats?.average);
        const count = Number(data.stats?.count);
        if (data.stats?.average == null || data.stats?.count == null || !Number.isFinite(average) || average < 0 || average > 5 || !Number.isInteger(count) || count < 0 || (count > 0 && average === 0)) {
          throw new Error('Invalid review summary');
        }
        if (lifecycleSignal.aborted) return;
        const stats = { average, count };
        hpReviewCache.set(key, { time: Date.now(), stats });
        this.displayReviewSummary(node, stats);
      } catch (error) {
        if (lifecycleSignal.aborted) return;
        // No made-up score or count on failure; keep the link to actual reviews.
        if (attempt < 2) {
          this.reviewTimers.push(window.setTimeout(() => {
            if (!lifecycleSignal.aborted) this.loadReviewSummary(node, attempt + 1, lifecycleSignal);
          }, (attempt + 1) * 1500));
        }
      } finally {
        window.clearTimeout(timeout);
        lifecycleSignal.removeEventListener('abort', cancel);
      }
    }

    displayReviewSummary(node, stats) {
      const hasReviews = stats.count > 0;
      const average = stats.average.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const count = stats.count.toLocaleString('de-DE');
      const stars = node.querySelector('[data-hp-review-stars]');
      const fill = node.querySelector('[data-hp-review-fill]');
      const value = node.querySelector('[data-hp-review-value]');
      const total = node.querySelector('[data-hp-review-count]');
      const status = node.querySelector('[data-hp-review-status]');
      if (stars) stars.hidden = !hasReviews;
      if (fill) fill.style.width = (stats.average / 5 * 100) + '%';
      if (value) { value.textContent = average + ' / 5'; value.hidden = !hasReviews; }
      if (total) { total.textContent = '(' + count + (stats.count === 1 ? ' Bewertung)' : ' Bewertungen)'); total.hidden = !hasReviews; }
      if (status) { status.hidden = hasReviews; status.textContent = 'Noch keine Bewertungen'; }
      const link = node.querySelector('[data-hp-review-link]');
      if (link) link.setAttribute('aria-label', hasReviews ? average + ' von 5 Sternen, ' + count + ' Bewertungen. Zu den Produktbewertungen' : 'Zu den Produktbewertungen');
    }

    readJson(selector, fallback) {
      const node = this.querySelector(selector);
      if (!node) return fallback;

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('HOLY PIT Refills: Ungültige Produktdaten.', error);
        return fallback;
      }
    }

    getInitialScent() {
      const checkedCard = this.querySelector('[data-hp-scent][aria-checked="true"]');
      if (checkedCard) return checkedCard.dataset.hpScent;

      const initial = this.variantMap.find((entry) => String(entry.id) === this.section.dataset.initialVariant);
      if (initial) return initial.scent;
      const available = this.variantMap.find((entry) => entry.available);
      return available ? available.scent : this.variantMap[0].scent;
    }

    activateInfoTab(tab, focus = false) {
      const dialog = tab.closest('.hp-v2-info-dialog');
      if (!dialog || !this.contains(dialog)) return;
      dialog.querySelectorAll('[data-hp-info-tab]').forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      dialog.querySelectorAll('[data-hp-info-panel]').forEach((panel) => {
        panel.hidden = panel.id !== tab.getAttribute('aria-controls');
      });
      if (focus) tab.focus();
    }

    onKeyDown(event) {
      const infoTab = event.target.closest('[data-hp-info-tab]');
      if (infoTab && this.contains(infoTab)) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const tabs = Array.from(infoTab.closest('[role="tablist"]').querySelectorAll('[data-hp-info-tab]'));
        let next = (tabs.indexOf(infoTab) + (event.key === 'ArrowLeft' ? -1 : 1) + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        this.activateInfoTab(tabs[next], true);
        return;
      }
      const card = event.target.closest('[role="radio"]');
      if (!card || !this.contains(card)) return;
      const group = card.closest('[role="radiogroup"]');
      if (!group || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      const cards = Array.from(group.querySelectorAll('[role="radio"]')).filter((item) => item.getAttribute('aria-disabled') !== 'true');
      if (!cards.length) return;
      event.preventDefault();
      const direction = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1;
      let index = (cards.indexOf(card) + direction + cards.length) % cards.length;
      if (event.key === 'Home') index = 0;
      if (event.key === 'End') index = cards.length - 1;
      cards[index].click();
      cards[index].focus();
    }

    onClick(event) {
      const infoTab = event.target.closest('[data-hp-info-tab]');
      if (infoTab && this.contains(infoTab)) {
        this.activateInfoTab(infoTab);
        return;
      }
      const browse = event.target.closest('[data-hp-scent-prev], [data-hp-scent-next]');
      if (browse && this.contains(browse)) {
        this.browseScent(browse.hasAttribute('data-hp-scent-prev') ? -1 : 1);
        return;
      }
      const choose = event.target.closest('[data-hp-scent-choose]');
      if (choose && this.contains(choose)) {
        const scent = this.dialogScents()[this.dialogScentIndex];
        const card = Array.from(this.querySelectorAll('[data-hp-scent]')).find((node) => node.dataset.hpScent === scent);
        if (card && card.getAttribute('aria-disabled') !== 'true') {
          // Reuse the exact same selection path as a direct scent-card click.
          card.click();
          this.querySelector('[data-hp-scent-dialog]').close();
          card.focus();
          card.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
        return;
      }
      const paymentOpen = event.target.closest('[data-hp-payment-open]');
      if (paymentOpen && this.contains(paymentOpen)) {
        this.openPaymentDialog(paymentOpen);
        return;
      }

      const paymentClose = event.target.closest('[data-hp-payment-close]');
      if (this.paymentDialog && (paymentClose || event.target === this.paymentDialog)) {
        this.closePaymentDialog();
        return;
      }

      const popupOpen = event.target.closest('[data-hp-popup-open]');
      if (popupOpen && this.contains(popupOpen)) {
        const dialog = document.getElementById(popupOpen.dataset.hpPopupOpen);
        if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
        return;
      }

      const popupClose = event.target.closest('[data-hp-popup-close]');
      if (popupClose && this.contains(popupClose)) {
        const dialog = popupClose.closest('dialog');
        if (dialog) dialog.close();
        return;
      }

      const quantityButton = event.target.closest('[data-hp-quantity-change]');
      if (quantityButton && this.contains(quantityButton)) {
        this.changeQuantity(Number(quantityButton.dataset.hpQuantityChange));
        return;
      }

      const moreButton = event.target.closest('[data-hp-scent-more]');
      if (moreButton && this.contains(moreButton)) {
        this.openScentDialog();
        return;
      }

      const closeButton = event.target.closest('[data-hp-scent-close]');
      if (closeButton && this.contains(closeButton)) {
        const dialog = this.querySelector('[data-hp-scent-dialog]');
        if (dialog) dialog.close();
        return;
      }

      const scentCard = event.target.closest('[data-hp-scent]');
      if (scentCard && this.contains(scentCard)) {
        if (scentCard.getAttribute('aria-disabled') === 'true') return;
        this.selectedScent = scentCard.dataset.hpScent;
        this.render();
        return;
      }

      const packCard = event.target.closest('[data-hp-pack]');
      if (packCard && this.contains(packCard)) {
        if (packCard.getAttribute('aria-disabled') === 'true') return;
        this.selectedPack = packCard.dataset.hpPack;
        this.render();
      }
    }

    onSectionClick(event) {
      const aboOpenButton = event.target.closest('[data-hp-abo-open]');
      const aboCloseButton = event.target.closest('[data-hp-abo-close]');
      const aboDialog = this.section.querySelector('[data-hp-abo-dialog]');

      if (aboOpenButton && aboDialog) {
        this.openAboDialog(aboOpenButton);
        return;
      }

      if (aboDialog && (aboCloseButton || event.target === aboDialog)) {
        this.closeAboDialog();
        return;
      }

      const openButton = event.target.closest('[data-hp-video-highlight-open]');
      const closeButton = event.target.closest('[data-hp-video-highlight-close]');
      const dialog = this.section.querySelector('[data-hp-video-highlight-dialog]');
      if (!dialog) return;

      if (openButton) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        return;
      }

      if (closeButton || event.target === dialog) this.closeMediaHighlight(dialog);
    }

    closeMediaHighlight(dialog) {
      const video = dialog.querySelector('video');
      if (video) video.pause();
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }

    initializeMediaHighlight() {
      this.mediaHighlight = this.section.querySelector('[data-hp-video-highlight-open]');
      this.mediaCarousel = this.section.querySelector('flickity-carousel');
      this.mediaPrevious = this.section.querySelector('[data-hp-media-previous]');
      this.mediaNext = this.section.querySelector('[data-hp-media-next]');
      if (!this.mediaCarousel) return;

      this.mediaHighlightHandler = () => window.requestAnimationFrame(() => this.updateMediaControls());
      this.mediaCarousel.addEventListener('flickity:ready', this.mediaHighlightHandler);
      this.mediaCarousel.addEventListener('flickity:slide-changed', this.mediaHighlightHandler);
      const dialog = this.section.querySelector('[data-hp-video-highlight-dialog]');
      if (dialog) dialog.addEventListener('close', () => {
        const video = dialog.querySelector('video');
        if (video) video.pause();
      });
      this.updateMediaControls();
    }

    async updateMediaControls() {
      if (!this.mediaCarousel) return;

      let selected = this.mediaCarousel.querySelector('.product__media-item.is-selected');
      let selectedIndex = 0;
      let cellCount = this.mediaCarousel.querySelectorAll('.product__media-item:not(.is-filtered)').length;
      if (this.mediaCarousel.flickityInstance) {
        const flickity = await this.mediaCarousel.flickityInstance;
        if (flickity) {
          if (flickity.selectedElement) selected = flickity.selectedElement;
          selectedIndex = Number(flickity.selectedIndex || 0);
          cellCount = flickity.cells ? flickity.cells.length : cellCount;
        }
      }

      const position = selected ? Number(selected.dataset.originalPosition || 0) : 0;
      if (this.mediaHighlight) {
        this.mediaHighlight.classList.toggle('is-hidden', position >= 3);
        this.mediaHighlight.setAttribute('aria-hidden', String(position >= 3));
        this.mediaHighlight.tabIndex = position >= 3 ? -1 : 0;
      }
      if (this.mediaPrevious) this.mediaPrevious.hidden = selectedIndex <= 0;
      if (this.mediaNext) this.mediaNext.hidden = cellCount <= 1 || selectedIndex >= cellCount - 1;
    }

    updatePackSelectionState() {
      this.querySelectorAll('[data-hp-pack-option]').forEach((option) => {
        const isSelected = option.dataset.hpPackOption === this.selectedPack;
        option.classList.toggle('is-selected', isSelected);
      });
    }

    initializeAboBox() {
      this.aboDialog = this.section.querySelector('[data-hp-abo-dialog]');
      if (!this.aboDialog) return;

      this.aboCloseHandler = () => {
        document.documentElement.classList.remove('hp-v2-dialog-open');
        document.body.classList.remove('hp-v2-dialog-open');
        if (this.aboTrigger && typeof this.aboTrigger.focus === 'function') this.aboTrigger.focus();
        this.aboTrigger = null;
      };

      this.aboKeydownHandler = (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeAboDialog();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.aboDialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
          .filter((element) => !element.hidden && element.getClientRects().length);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      this.aboDialog.addEventListener('close', this.aboCloseHandler);
      this.aboDialog.addEventListener('keydown', this.aboKeydownHandler);
    }

    openAboDialog(trigger) {
      if (!this.aboDialog) return;
      this.aboTrigger = trigger;
      document.documentElement.classList.add('hp-v2-dialog-open');
      document.body.classList.add('hp-v2-dialog-open');
      if (typeof this.aboDialog.showModal === 'function') this.aboDialog.showModal();
      else this.aboDialog.setAttribute('open', '');

      const firstControl = this.aboDialog.querySelector('[data-hp-abo-close], a[href], button:not([disabled])');
      if (firstControl) window.requestAnimationFrame(() => firstControl.focus());
    }

    closeAboDialog() {
      if (!this.aboDialog) return;
      if (typeof this.aboDialog.close === 'function') this.aboDialog.close();
      else {
        this.aboDialog.removeAttribute('open');
        this.aboCloseHandler();
      }
    }

    initializePaymentDialog() {
      this.paymentDialog = this.querySelector('[data-hp-payment-dialog]');
      if (!this.paymentDialog) return;

      this.paymentCloseHandler = () => {
        document.documentElement.classList.remove('hp-v2-dialog-open');
        document.body.classList.remove('hp-v2-dialog-open');
        if (this.paymentTrigger && typeof this.paymentTrigger.focus === 'function') this.paymentTrigger.focus();
        this.paymentTrigger = null;
      };

      this.paymentKeydownHandler = (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closePaymentDialog();
          return;
        }
        if (event.key !== 'Tab') return;

        const focusable = Array.from(this.paymentDialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
          .filter((element) => !element.hidden && element.getClientRects().length);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      this.paymentDialog.addEventListener('close', this.paymentCloseHandler);
      this.paymentDialog.addEventListener('keydown', this.paymentKeydownHandler);
    }

    openPaymentDialog(trigger) {
      if (!this.paymentDialog) return;
      this.paymentTrigger = trigger;
      document.documentElement.classList.add('hp-v2-dialog-open');
      document.body.classList.add('hp-v2-dialog-open');
      if (typeof this.paymentDialog.showModal === 'function') this.paymentDialog.showModal();
      else this.paymentDialog.setAttribute('open', '');

      const firstControl = this.paymentDialog.querySelector('[data-hp-payment-close], a[href], button:not([disabled])');
      if (firstControl) window.requestAnimationFrame(() => firstControl.focus());
    }

    closePaymentDialog() {
      if (!this.paymentDialog) return;
      if (typeof this.paymentDialog.close === 'function') this.paymentDialog.close();
      else {
        this.paymentDialog.removeAttribute('open');
        this.paymentCloseHandler();
      }
    }

    findSelection() {
      const matchingSingles = this.variantMap.filter((entry) => entry.scent === this.selectedScent);
      let single;

      /*
       * A product can temporarily contain more than one variant with the same scent.
       * Prefer the explicitly mapped and purchasable pair instead of the first array item.
       * This prevents a false unavailable state for the first matching scent.
       */
      if (this.selectedPack === 'bundle') {
        single = matchingSingles.find((entry) => entry.available && entry.bundle && entry.bundle.available)
          || matchingSingles.find((entry) => entry.available && entry.bundle)
          || matchingSingles.find((entry) => entry.available)
          || matchingSingles[0];
      } else {
        single = matchingSingles.find((entry) => entry.available)
          || matchingSingles[0];
      }

      if (!this.querySelector('[data-hp-scent]')) {
        single = this.variantMap.find((entry) => String(entry.id) === this.section.dataset.initialVariant) || single;
      }
      if (!single) return { single: null, active: null };

      if (this.selectedPack === 'bundle' && single.bundle && single.bundle.available) {
        return { single, active: single.bundle };
      }

      if (this.selectedPack === 'bundle' && (!single.bundle || !single.bundle.available)) {
        this.selectedPack = 'single';
      }

      return { single, active: single };
    }

    render() {
      const selection = this.findSelection();
      const single = selection.single;
      const active = selection.active;
      if (!single || !active) return;

      this.querySelectorAll('[data-hp-scent]').forEach((card) => {
        card.setAttribute('aria-checked', String(card.dataset.hpScent === this.selectedScent));
      });

      this.querySelectorAll('[data-hp-pack]').forEach((card) => {
        card.setAttribute('aria-checked', String(card.dataset.hpPack === this.selectedPack));
      });
      this.querySelectorAll('[role="radiogroup"]').forEach((group) => {
        const cards = Array.from(group.querySelectorAll('[role="radio"]'));
        const current = cards.find((card) => card.getAttribute('aria-checked') === 'true') || cards.find((card) => card.getAttribute('aria-disabled') !== 'true');
        cards.forEach((card) => { card.tabIndex = card === current ? 0 : -1; });
      });
      this.classList.toggle('is-bundle-selected', this.selectedPack === 'bundle');
      this.updatePackSelectionState();

      const bundleCard = this.querySelector('[data-hp-pack="bundle"]');
      const bundleAvailable = Boolean(single.bundle && single.bundle.available);
      if (bundleCard) {
        bundleCard.setAttribute('aria-disabled', String(!bundleAvailable));
        bundleCard.classList.toggle('is-unavailable', !bundleAvailable);
      }

      this.setText('[data-hp-single-price]', this.money(single.price));
      this.updateScentDescription(single);

      const bundlePrice = this.querySelector('[data-hp-bundle-price]');
      if (single.bundle && bundleAvailable) {
        this.setText('[data-hp-bundle-price]', this.money(Math.round(single.bundle.price / this.bundleSize)));
        const bundleUnit = this.querySelector('[data-hp-bundle-unit]');
        this.setText('[data-hp-bundle-unit]', bundleUnit ? bundleUnit.dataset.label || '' : '');
        if (bundlePrice) bundlePrice.hidden = false;
        this.updateSaving(single, single.bundle);
      } else {
        this.setText('[data-hp-bundle-price]', '');
        this.setText('[data-hp-bundle-unit]', 'Nicht verfügbar');
        if (bundlePrice) bundlePrice.hidden = true;
        const savingNode = this.querySelector('[data-hp-pack-summary] [data-hp-saving]');
        if (savingNode) savingNode.hidden = true;
        const bundleBadge = this.querySelector('[data-hp-bundle-badge]');
        if (bundleBadge) bundleBadge.textContent = bundleBadge.dataset.defaultLabel || 'Vorteilspreis';
      }

      this.variantInput.value = active.id;
      this.variantInput.dataset.productid = active.productId;
      this.submitButton.disabled = !active.available;
      this.submitButton.classList.toggle('button--ternary', !active.available);
      this.submitButton.classList.toggle('button--primary', active.available);

      this.setText('[data-hp-submit-label]', active.available ? this.submitButton.dataset.defaultLabel || 'In den Warenkorb' : 'Ausverkauft');
      this.updatePackSummary(active, single, this.selectedPack === 'bundle' ? this.bundleSize : 1);
      this.updateInventoryBlocks(active);

      /* Keep Focal media in sync with the selected scent's single variant. */
      const fullVariant = (this.productData.variants || []).find((variant) => Number(variant.id) === Number(single.id));
      if (fullVariant) {
        this.form.dispatchEvent(new CustomEvent('variant:changed', {
          bubbles: true,
          detail: { variant: fullVariant }
        }));
      }

      if (window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', single.id);
        window.history.replaceState({ path: url.toString() }, '', url.toString());
      }

    }

    updateScentDescription(single) {
      const info = this.querySelector('[data-hp-scent-info]');
      if (!info) return;
      const description = String(single.scentDescription || '').trim();
      // Keep a way to browse other scents even if this scent has no description.
      info.hidden = false;
      this.setText('[data-hp-scent-preview]', description ? description.replace(/(?:\.\.\.|…)$/, '') + '...' : 'Entdecke unsere Düfte...');
    }

    dialogScents() {
      return Array.from(this.querySelectorAll('[data-hp-scent]')).map((card) => card.dataset.hpScent);
    }

    browseScent(delta) {
      const scents = this.dialogScents();
      if (!scents.length) return;
      this.dialogScentIndex = ((this.dialogScentIndex || 0) + delta + scents.length) % scents.length;
      this.renderDialogScent();
    }

    renderDialogScent() {
      const scents = this.dialogScents();
      const scent = scents[this.dialogScentIndex];
      const matches = this.variantMap.filter((entry) => entry.scent === scent);
      const single = matches.find((entry) => entry.available) || matches[0];
      if (!single) return;
      this.setText('[data-hp-scent-dialog-title]', scent);
      this.setText('[data-hp-scent-dialog-body]', single.scentDescription || 'Für diesen Duft ist noch keine Beschreibung hinterlegt.');
      this.setText('[data-hp-scent-position]', `${this.dialogScentIndex + 1} / ${scents.length}`);
      const card = Array.from(this.querySelectorAll('[data-hp-scent]')).find((node) => node.dataset.hpScent === scent);
      const choose = this.querySelector('[data-hp-scent-choose]');
      if (choose) {
        choose.disabled = !card || card.getAttribute('aria-disabled') === 'true';
        choose.textContent = choose.disabled ? 'Ausverkauft' : 'Duft wählen';
      }
      this.querySelectorAll('[data-hp-scent-prev], [data-hp-scent-next]').forEach((button) => { button.disabled = scents.length < 2; });
      const media = this.querySelector('[data-hp-scent-dialog-media]');
      const image = this.querySelector('[data-hp-scent-dialog-image]');
      if (media && image) {
        media.hidden = !single.featuredImage;
        if (single.featuredImage) {
          image.src = single.featuredImage;
          image.alt = `Duftvariante ${scent}`;
        } else {
          image.removeAttribute('src');
          image.alt = '';
        }
      }
    }

    openScentDialog() {
      const dialog = this.querySelector('[data-hp-scent-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      this.dialogScentIndex = Math.max(0, this.dialogScents().indexOf(this.selectedScent));
      this.renderDialogScent();
      if (!dialog.open) dialog.showModal();
    }

    unitPriceFor(active, single, refillCount) {
      if (Number(active.unitPrice) > 0 && active.unitReferenceUnit) {
        return { cents: active.unitPrice, value: active.unitReferenceValue || 1, unit: active.unitReferenceUnit };
      }
      // No assumed product weight: only the single's actual Shopify measurement.
      if (refillCount <= 1 || !(Number(single.quantityValue) > 0)) return null;
      const from = single.quantityUnit;
      const to = single.unitReferenceUnit;
      const factors = { 'g:kg': 1000, 'kg:g': 0.001, 'ml:l': 1000, 'l:ml': 0.001 };
      const factor = from && from === to ? 1 : factors[`${from}:${to}`];
      if (!factor || !to) return null;
      const value = Number(single.unitReferenceValue) || 1;
      return { cents: Math.round(active.price * factor * value / (single.quantityValue * refillCount)), value, unit: to };
    }

    updatePackSummary(active, single, refillCount) {
      const summary = this.querySelector('[data-hp-pack-summary]');
      if (!summary || !active) return;
      const quantity = Math.max(1, Number(this.querySelector('[data-hp-quantity]')?.value) || 1);
      summary.dataset.hpPackType = this.selectedPack;
      this.setText('[data-hp-current-price]', this.money(active.price * quantity));
      const scent = this.productData.title && this.productData.variants?.length === 1 && single.title === 'Default Title'
        ? this.productData.title : this.selectedScent;
      this.setText('[data-hp-cart-summary]', `${refillCount * quantity}× Refill ${scent || this.productData.title || ''}`);
      const compare = summary.querySelector('[data-hp-summary-compare]');
      const reference = this.selectedPack === 'bundle' ? single.price * this.bundleSize * quantity : 0;
      if (compare) {
        compare.hidden = !(reference > active.price * quantity);
        compare.textContent = compare.hidden ? '' : this.money(reference);
      }
      const unitNode = summary.querySelector('[data-hp-unit-price]');
      const unit = this.unitPriceFor(active, single, refillCount);
      if (unitNode) {
        unitNode.hidden = !unit;
        unitNode.textContent = unit ? `${this.money(unit.cents)}/${Number(unit.value) === 1 ? '' : unit.value + ' '}${unit.unit}` : '';
      }
      if (single.bundle) this.updateSaving(single, single.bundle);
    }

    updateSaving(single, bundle) {
      const reference = single.price * this.bundleSize;
      const saving = Math.max(0, reference - bundle.price);
      const summary = this.querySelector('[data-hp-pack-summary]');
      const savingNode = summary && summary.querySelector('[data-hp-saving]');
      const savingText = summary && summary.querySelector('[data-hp-saving-text]');
      const bundleBadge = this.querySelector('[data-hp-bundle-badge]');

      if (savingNode) savingNode.hidden = this.selectedPack !== 'bundle' || !saving;
      if (savingText && saving) {
        const quantity = Math.max(1, Number(this.querySelector('[data-hp-quantity]')?.value) || 1);
        savingText.textContent = `Du sparst ${this.money(saving * quantity)}`;
      }

      if (!saving) {
        if (bundleBadge) bundleBadge.textContent = bundleBadge.dataset.defaultLabel || 'Vorteilspreis';
        return;
      }

      const percent = Math.round((saving / reference) * 100);
      if (bundleBadge) bundleBadge.textContent = `${percent}% Rabatt`;
    }

    updateInventoryBlocks(active) {
      this.querySelectorAll('[data-hp-inventory]').forEach((inventory) => {
        const textNode = inventory.querySelector('[data-hp-inventory-text]');
        const threshold = Number(inventory.dataset.lowThreshold || 5);
        const quantity = Number(active.inventoryQuantity || 0);
        const isManaged = Boolean(active.inventoryManagement);
        const isLow = active.available && isManaged && active.inventoryPolicy !== 'continue' && quantity > 0 && quantity <= threshold;
        let message = active.available ? inventory.dataset.availableText : inventory.dataset.soldOutText;

        if (isLow) message = (inventory.dataset.lowText || '').replace('[count]', quantity);
        if (textNode) textNode.textContent = message;
        inventory.classList.toggle('is-available', active.available && !isLow);
        inventory.classList.toggle('is-low', isLow);
        inventory.classList.toggle('is-unavailable', !active.available);
      });
    }

    changeQuantity(delta) {
      const input = this.querySelector('[data-hp-quantity]');
      if (!input || !Number.isFinite(delta)) return;

      const nextQuantity = Math.max(1, Math.min(99, Number(input.value || 1) + delta));
      input.value = String(nextQuantity);
      const { active, single } = this.findSelection();
      if (active && single) this.updatePackSummary(active, single, this.selectedPack === 'bundle' ? this.bundleSize : 1);
      this.querySelectorAll('[data-hp-quantity-output]').forEach((output) => {
        output.textContent = String(nextQuantity);
      });
    }

    initializeCountdowns() {
      this.countdownTimers = [];
      this.querySelectorAll('[data-hp-countdown]').forEach((countdown) => {
        const endTime = Date.parse(countdown.dataset.end || '');
        const output = countdown.querySelector('[data-hp-countdown-value]');

        if (!Number.isFinite(endTime) || !output) {
          countdown.hidden = true;
          return;
        }

        const update = () => {
          const remaining = endTime - Date.now();
          if (remaining <= 0) {
            output.textContent = countdown.dataset.expiredText || 'Beendet';
            return;
          }

          const totalSeconds = Math.floor(remaining / 1000);
          const days = Math.floor(totalSeconds / 86400);
          const hours = Math.floor((totalSeconds % 86400) / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          const time = [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
          output.textContent = days > 0 ? `${days}T ${time}` : time;
        };

        update();
        this.countdownTimers.push(window.setInterval(update, 1000));
      });
    }

    removeSamplePreviewText() {
      /* Some editor/app previews inject this placeholder as a standalone label. */
      this.querySelectorAll('*').forEach((node) => {
        if (node.children.length === 0 && node.textContent.trim().toLowerCase() === 'sample preview') {
          node.hidden = true;
        }
      });
    }

    onAdded(event) {
      const added = event.detail && event.detail.variant;
      const selection = this.findSelection();
      const quantityInput = this.querySelector('[data-hp-quantity]');
      const submittedQuantity = Math.max(1, Number(quantityInput && quantityInput.value) || 1);
      /*
       * A separate success event avoids claiming a successful add before Shopify confirms it.
       * Existing tracking remains untouched; GTM can consume this event after preview validation.
       */
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'hp_v2_add_to_cart_success',
        hp_pack_type: this.selectedPack,
        hp_scent: this.selectedScent,
        product_id: selection.active ? selection.active.productId : null,
        variant_id: selection.active ? selection.active.id : null,
        sku: selection.active ? selection.active.sku : null,
        price: selection.active ? selection.active.price / 100 : null,
        quantity: submittedQuantity,
        shopify_response_id: added && added.id ? added.id : null
      });
    }

    money(cents) {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format((Number(cents) || 0) / 100);
    }

    setText(selector, value) {
      const node = this.querySelector(selector);
      if (node) node.textContent = value;
    }

    hide(selector) {
      const node = this.querySelector(selector);
      if (node) node.hidden = true;
    }

  }

  window.customElements.define('hp-refill-selector', HpRefillSelector);
})();
